"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Partager un lien : la feuille du système quand elle existe, le presse-papier
 * sinon.
 *
 * # Pourquoi les deux, et dans cet ordre
 *
 * Sur un téléphone, `navigator.share` ouvre la feuille de partage du système,
 * qui connaît les applications installées — c'est le geste qu'on attend, et
 * aucune page ne peut le remplacer. Sur un poste de bureau elle n'existe
 * généralement pas, et le presse-papier est alors le partage : on colle où l'on
 * veut. Refusée ou refermée sans choisir, la feuille retombe aussi sur la
 * copie : mieux vaut un lien dans le presse-papier qu'un geste sans effet.
 *
 * # `copied` ne dure que deux secondes
 *
 * C'est un accusé de réception, pas un état : le bouton redit « Partager »
 * ensuite, parce qu'on peut vouloir recommencer. Le minuteur est annulé au
 * démontage — un composant qui disparaît pendant ces deux secondes (une prise
 * qu'on vient de supprimer) laisserait sinon un `setState` sans destinataire.
 *
 * ```tsx
 * const { copied, share } = useShareLink();
 * <button onClick={() => void share(url, { title })}>{copied ? "Lien copié" : "Partager"}</button>
 * ```
 */
export function useShareLink(): {
  copied: boolean;
  share: (url: string, about?: { title?: string; text?: string }) => Promise<void>;
} {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    },
    [],
  );

  const share = useCallback(async (url: string, about: { title?: string; text?: string } = {}) => {
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ ...about, url });

        return;
      } catch {
        // Refusée ou fermée : la copie reste.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);

      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }

      timer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Presse-papier refusé : le lien reste lisible à l'écran.
    }
  }, []);

  return { copied, share };
}
